import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Send, Download, FileCheck, Copy, Loader2, Pencil, Mic, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, eur, dateFr, dateTimeFr, downloadFile, apiError } from "@/lib/api";
import StatutBadge from "@/components/StatutBadge";
import Modal from "@/components/Modal";

export default function DevisDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [devis, setDevis] = useState(null);
  const [envoiOpen, setEnvoiOpen] = useState(false);
  const [canal, setCanal] = useState("email");
  const [busy, setBusy] = useState(false);
  const [supprOpen, setSupprOpen] = useState(false);

  const load = () => api.get(`/devis/${id}`).then((r) => setDevis(r.data)).catch(() => navigate("/devis"));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!devis) return <div className="py-20 text-center text-stone-500">{t("commun.chargement")}</div>;

  async function envoyer() {
    setBusy(true);
    try {
      await api.post(`/devis/${id}/envoyer`, { canal });
      toast.success(t("devis_detail.envoye_email"));
      setEnvoiOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function convertir() {
    setBusy(true);
    try {
      const { data } = await api.post(`/devis/${id}/convertir`);
      toast.success(t("devis_detail.converti", { numero: data.numero }));
      navigate(`/factures/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  }

  async function supprimer() {
    setBusy(true);
    try {
      await api.delete(`/devis/${id}`);
      toast.success(t("devis_detail.supprime", { numero: devis.numero }));
      navigate("/devis");
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
      setSupprOpen(false);
    }
  }

  return (
    <div className="space-y-5 max-w-3xl" data-testid="page-devis-detail">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/devis")} className="w-11 h-11 flex items-center justify-center rounded-lg bg-white border border-[#E6DFD1]"
                data-testid="btn-retour">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="font-serif font-bold text-2xl sm:text-3xl">{t("dash.devis")} {devis.numero}</h1>
          <p className="text-stone-500 text-sm">{devis.client?.nom} · {dateFr(devis.cree_le)}</p>
        </div>
        <StatutBadge statut={devis.statut} testid="badge-statut-devis" />
      </div>

      {devis.objet && (
        <p className="font-medium text-[#8A5A2B]" data-testid="objet-devis">{devis.objet}</p>
      )}

      {devis.chantier && (
        <div className="panel !py-3 text-sm">
          <span className="font-semibold">{t("devis_detail.chantier_label")} :</span> {devis.chantier.titre} — {devis.chantier.adresse}
        </div>
      )}

      <div className="panel !p-0 overflow-hidden">
        <div className="divide-y divide-[#E6DFD1]">
          {devis.lignes.map((l, i) => (
            <div key={i} className="flex justify-between gap-3 p-4 text-sm">
              <div>
                <div className="font-medium">{l.designation}</div>
                <div className="text-stone-500 text-xs">{l.quantite}{l.unite ? ` ${l.unite}` : ""} × {eur(l.prix_unitaire)}</div>
              </div>
              <span className="font-serif font-semibold shrink-0">{eur(l.quantite * l.prix_unitaire)}</span>
            </div>
          ))}
          {((devis.mode_mo === "horaire" && Number(devis.heures_mo) > 0)
            || (devis.mode_mo !== "horaire" && Number(devis.montant_forfait_mo) > 0)) && (
            <div className="flex justify-between gap-3 p-4 text-sm" data-testid="ligne-main-oeuvre">
              <div>
                <div className="font-medium">{t("devis_form.main_oeuvre")}</div>
                <div className="text-stone-500 text-xs">{devis.mode_mo === "horaire"
                  ? `${devis.heures_mo} h × ${eur(devis.taux_horaire_mo)}/h`
                  : t("devis_form.mode_forfait")}</div>
              </div>
              <span className="font-serif font-semibold shrink-0">{eur(devis.mode_mo === "horaire"
                ? (devis.heures_mo || 0) * (devis.taux_horaire_mo || 0)
                : (devis.montant_forfait_mo || 0))}</span>
            </div>
          )}
        </div>
        <div className="bg-[#12100D] p-5" data-testid="bloc-total-detail">
          <div className="flex justify-between text-stone-400 text-sm">
            <span>{t("devis_form.total_ht")}</span><span className="font-serif">{eur(devis.total_ht)}</span>
          </div>
          {(devis.ventilation_tva || [{ taux: devis.tva, base_ht: devis.total_ht, montant_tva: devis.total_ttc - devis.total_ht, autoliquidation: false }]).map((v) => (
            <div key={v.autoliquidation ? "AL" : v.taux} className="flex justify-between text-stone-400 text-sm mt-1"
                 data-testid={`devis-tva-${v.autoliquidation ? "al" : String(v.taux).replace(".", "-")}`}>
              <span>{v.autoliquidation ? t("devis_form.al_zero") : t("devis_form.tva_ligne", { taux: String(v.taux).replace(".", ","), base: eur(v.base_ht) })}</span>
              <span className="font-serif">{eur(v.montant_tva)}</span>
            </div>
          ))}
          {devis.autoliquidation && (
            <p className="text-[#D9A461] text-xs mt-2 font-semibold" data-testid="mention-autoliquidation">
              {t("devis_form.al_mention")}
            </p>
          )}
          <div className="flex justify-between items-center mt-3 pt-3 border-t border-white/10">
            <span className="text-white font-semibold">{t("devis_form.total_ttc")}</span>
            <span className="amount-display">{eur(devis.total_ttc)}</span>
          </div>
        </div>
      </div>

      <div className="panel text-sm space-y-1">
        <div className="font-semibold mb-1">{t("devis_detail.lien_client")}</div>
        <div className="flex gap-2">
          <input readOnly className="input-field !h-10 text-xs" value={devis.lien_public} data-testid="input-lien-public" />
          <button onClick={() => { navigator.clipboard.writeText(devis.lien_public); toast.success(t("devis_detail.lien_copie")); }}
                  className="btn-secondary !h-10 !px-3 shrink-0" data-testid="btn-copier-lien">
            <Copy size={16} />
          </button>
        </div>
      </div>

      {devis.signature?.data && (
        <div className="panel" data-testid="bloc-signature">
          <div className="font-semibold text-sm mb-2">{t("devis_detail.signature_titre")}</div>
          <img src={devis.signature.data} alt={t("devis_detail.signature_titre")}
               className="h-20 border border-[#E6DFD1] rounded-lg bg-white" />
          <p className="text-xs text-stone-500 mt-2">
            {t("devis_detail.signe")}{devis.signature.nom && ` ${t("devis_detail.par")} ${devis.signature.nom}`} {t("devis_detail.le")} {dateTimeFr(devis.signature.date)}
            {devis.signature.ip && ` — IP ${devis.signature.ip}`}. {t("devis_detail.signature_preuve")}
          </p>
        </div>
      )}

      {devis.comptes_rendus?.length > 0 && (
        <div className="panel" data-testid="bloc-cr-devis">
          <div className="font-semibold text-sm mb-2">{t("devis_detail.cr_titre")}</div>
          <div className="divide-y divide-[#E6DFD1]">
            {devis.comptes_rendus.map((cr) => (
              <Link key={cr.id} to={`/comptes-rendus?ouvrir=${cr.id}`}
                    className="flex items-center gap-2 py-2.5 text-sm text-[#8A5A2B] hover:underline"
                    data-testid={`lien-cr-${cr.id}`}>
                <Mic size={15} className="shrink-0" />
                <span className="flex-1 min-w-0 truncate">{cr.titre}</span>
                <span className="text-stone-400 text-xs shrink-0">{dateFr(cr.cree_le)}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {devis.statut === "brouillon" && (
          <button onClick={() => navigate(`/devis/${id}/modifier`)} className="btn-secondary"
                  data-testid="btn-modifier-devis">
            <Pencil size={18} /> {t("devis_detail.modifier")}
          </button>
        )}
        {devis.statut === "brouillon" && (
          <button onClick={() => setSupprOpen(true)} className="btn-secondary !text-[#A32E20]"
                  data-testid="btn-supprimer-devis">
            <Trash2 size={18} /> {t("devis_detail.supprimer")}
          </button>
        )}
        <button onClick={() => downloadFile(`/devis/${id}/pdf`, `${devis.numero}.pdf`)} className="btn-secondary"
                data-testid="btn-telecharger-pdf">
          <Download size={18} /> {t("devis_detail.pdf")}
        </button>
        {devis.statut === "accepte" && !devis.facture ? (
          <button onClick={convertir} disabled={busy} className="btn-accent" data-testid="btn-convertir-facture">
            {busy ? <Loader2 className="animate-spin" size={18} /> : <FileCheck size={18} />} {t("devis_detail.convertir")}
          </button>
        ) : devis.facture ? (
          <Link to={`/factures/${devis.facture.id}`} className="btn-secondary" data-testid="link-facture-liee">
            <FileCheck size={18} /> {t("devis_detail.voir_facture", { numero: devis.facture.numero })}
          </Link>
        ) : (
          <button onClick={() => setEnvoiOpen(true)} className="btn-accent" data-testid="btn-envoyer-devis">
            <Send size={18} /> {t("devis_detail.envoyer")}
          </button>
        )}
      </div>

      <Modal open={envoiOpen} onClose={() => setEnvoiOpen(false)} title={t("devis_detail.envoyer")} testid="modal-envoi-devis">
        <p className="text-sm text-stone-600 mb-4">{t("devis_detail.envoi_texte")}</p>
        <div className="grid grid-cols-1 gap-2 mb-5">
          {[["email", "Email"]].map(([v, l]) => (
            <button key={v} onClick={() => setCanal(v)} data-testid={`canal-${v}`}
                    className={`h-12 rounded-lg font-semibold text-sm border ${
                      canal === v ? "bg-[#12100D] text-[#D9A461] border-[#D9A461]/40" : "bg-white border-[#E6DFD1] text-stone-600"}`}>
              {l}
            </button>
          ))}
        </div>
        <button onClick={envoyer} disabled={busy} className="btn-accent w-full" data-testid="btn-confirmer-envoi">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />} {t("devis_detail.envoyer_email")}
        </button>
      </Modal>

      <Modal open={supprOpen} onClose={() => setSupprOpen(false)} title={t("devis_detail.supprimer_titre")} testid="modal-supprimer-devis">
        <p className="text-sm text-stone-600 mb-5">{t("devis_detail.supprimer_texte", { numero: devis.numero })}</p>
        <button onClick={supprimer} disabled={busy} className="btn-accent w-full !bg-[#A32E20]" data-testid="btn-confirmer-suppression-devis">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <Trash2 size={18} />} {t("devis_detail.supprimer_confirmer")}
        </button>
      </Modal>
    </div>
  );
}
